import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface ImpersonationTarget {
  id: string;
  email: string;
  name?: string | null;
  is_active: boolean;
  organizations: { id: string; name: string; role: string }[];
}

interface ImpersonationState {
  /** Sent as X-Impersonate-User-Id on every request while set. */
  targetUserId: string | null;
  target: ImpersonationTarget | null;
  startedAt: string | null;
  setTarget: (target: ImpersonationTarget) => void;
  clear: () => void;
}

/**
 * Who the superuser is currently viewing the app as.
 *
 * Persisted so a page refresh doesn't silently drop you back into your own
 * account while the banner is still what you last remember seeing — the same
 * reasoning behind persisting selectedOrganizationId.
 */
export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set) => ({
      targetUserId: null,
      target: null,
      startedAt: null,
      setTarget: (target) =>
        set({
          targetUserId: target.id,
          target,
          startedAt: new Date().toISOString(),
        }),
      clear: () => set({ targetUserId: null, target: null, startedAt: null }),
    }),
    {
      name: "impersonation-storage",
    }
  )
);

/** Read impersonation state outside React (interceptors, plain modules). */
export const isImpersonating = () =>
  useImpersonationStore.getState().targetUserId !== null;
